import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { 
  Button, 
  Card, 
  Table, 
  Input, 
  Select, 
  Row, 
  Col,
  Space,
  Tag,
  Typography,
  Statistic,
  Divider,
  Checkbox
} from "antd";
import { 
  HomeOutlined, 
  LogoutOutlined,
  SearchOutlined,
  FilterOutlined,
  FileTextOutlined,
  BarChartOutlined,
  ReloadOutlined,
  ShoppingOutlined,
  DollarOutlined,
  InboxOutlined
} from "@ant-design/icons";
import { ToastContainer, toast } from "react-toastify";
import Layout from "../../layout/Layout";
import fetchData from "../../functions/fetchData";
import ExportButtons from "../../components/ExportButtons/ExportButtons";
import "react-toastify/dist/ReactToastify.css";
import axios from 'axios';
import { getHeaders } from '../../utility/getHeader';
import logo from '../../assets/logo.png';

const { Option } = Select;
const { Title, Text } = Typography;
const { Search } = Input;

export default function ClosingStock() {
  const userType = (localStorage.getItem('usertype') || sessionStorage.getItem('usertype') || '').toLowerCase();
  const navigate = useNavigate();
  
  const [stockData, setStockData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    category: "",
    searchText: ""
  });
  const [showZeroStock, setShowZeroStock] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(20);
  const [exportFullColumns, setExportFullColumns] = useState(false);

  // Fetch closing stock data
  useEffect(() => {
    loadClosingStockData();
  }, []);

  // Auto-apply filters when filters, stockData, or showZeroStock changes
  useEffect(() => {
    applyFilters();
  }, [filters.category, filters.searchText, showZeroStock, stockData]);

  const loadClosingStockData = async () => {
    setLoading(true);
    try {
      const response = await axios.get('/warehousemanager/closing-stock', {
        ...getHeaders()
      });

      if (response.data && response.data.data) {
        const stockItems = response.data.data;
        setStockData(stockItems);
        setFilteredData(stockItems);
        toast.success('Closing stock data loaded successfully');
      } else {
        // Fallback: fetch from wstock table directly
        await fetchData("wstock", (data) => {
          // Group by item and calculate closing stock
          const groupedStock = groupStockByItem(data);
          setStockData(groupedStock);
          setFilteredData(groupedStock);
        }, "name", {});
      }
    } catch (error) {
      console.error('Error fetching closing stock:', error);
      // Fallback to direct wstock query
      await fetchData("wstock", (data) => {
        const groupedStock = groupStockByItem(data);
        setStockData(groupedStock);
        setFilteredData(groupedStock);
      }, "name", {});
    } finally {
      setLoading(false);
    }
  };

  // Group stock data by item name
  const groupStockByItem = (data) => {
    const grouped = {};
    
    // Sort data by ID to ensure we get the last entry correctly (highest ID = most recent)
    const sortedData = [...data].sort((a, b) => {
      const idA = parseInt(a.id || 0);
      const idB = parseInt(b.id || 0);
      return idA - idB;
    });
    
    sortedData.forEach(item => {
      const itemName = item.name || 'Unknown';
      
      if (!grouped[itemName]) {
        grouped[itemName] = {
          item_name: itemName,
          category: item.category || 'Uncategorized',
          quantity: 0,
          prices: [], // Store all prices for average calculation
          total_amount: 0,
          balance_qty: 0, // Closing stock from last entry's total
          lastEntry: null
        };
      }
      
      // Store price for average calculation (only non-zero prices)
      const rate = parseFloat(item.rate || 0);
      if (rate > 0) {
        grouped[itemName].prices.push(rate);
      }
      
      // Add quantity (stock in) or subtract (stock out)
      const qty = parseFloat(item.qty || 0);
      if (item.sname && item.sname.toLowerCase() === 'club') {
        grouped[itemName].quantity -= qty; // Stock out
      } else {
        grouped[itemName].quantity += qty; // Stock in
      }
      
      // Keep track of the last entry
      grouped[itemName].lastEntry = item;
    });

    // Convert to array and calculate averages and balance qty
    return Object.values(grouped).map(item => {
      // Calculate average price
      const averagePrice = item.prices.length > 0 
        ? item.prices.reduce((sum, price) => sum + price, 0) / item.prices.length
        : 0;
      
      // Get closing stock from last entry's total field (last ID entry)
      const balanceQty = item.lastEntry && item.lastEntry.total 
        ? parseFloat(item.lastEntry.total) 
        : 0;
      
      return {
        item_name: item.item_name,
        category: item.category,
        quantity: item.quantity,
        balance_qty: balanceQty, // Closing stock from last entry's total column
        price: averagePrice, // Average price
        total_amount: balanceQty * averagePrice
      };
    });
  };

  // Get unique categories
  const getUniqueCategories = () => {
    const categories = stockData.map(item => item.category).filter(Boolean);
    return [...new Set(categories)].sort();
  };

  // Apply filters
  const applyFilters = () => {
    let filtered = [...stockData];

    // Filter by category
    if (filters.category) {
      filtered = filtered.filter(item => 
        item.category && item.category.toLowerCase() === filters.category.toLowerCase()
      );
    }

    // Filter by search text
    if (filters.searchText) {
      const searchLower = filters.searchText.toLowerCase();
      filtered = filtered.filter(item =>
        (item.item_name && item.item_name.toLowerCase().includes(searchLower)) ||
        (item.category && item.category.toLowerCase().includes(searchLower))
      );
    }

    // Filter zero stock items
    if (!showZeroStock) {
      filtered = filtered.filter(item => parseFloat(item.balance_qty || 0) > 0);
    }

    setFilteredData(filtered);
    setCurrentPage(1);
  };

  // Reset filters
  const resetFilters = () => {
    setFilters({
      category: "",
      searchText: ""
    });
    setShowZeroStock(false);
    setFilteredData(stockData);
    setCurrentPage(1);
  };

  // Handle filter change
  const handleFilterChange = (name, value) => {
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  // Handle logout
  const handleLogout = () => {
    localStorage.clear();
    sessionStorage.clear();
    navigate('/login');
  };

  // Calculate statistics
  const totalItems = filteredData.length;
  const totalQuantity = filteredData.reduce((sum, item) => sum + parseFloat(item.balance_qty || 0), 0);
  const totalValue = filteredData.reduce((sum, item) => sum + parseFloat(item.total_amount || 0), 0);
  const zeroStockItems = stockData.filter(item => parseFloat(item.balance_qty || 0) === 0).length;

  // Table columns
  const columns = [
    {
      title: 'S.No.',
      key: 'sno',
      align: 'center',
      width: 80,
      render: (text, record, index) => (currentPage - 1) * itemsPerPage + index + 1
    },
    {
      title: 'Item Name',
      dataIndex: 'item_name',
      key: 'item_name',
      align: 'left',
      sorter: (a, b) => (a.item_name || '').localeCompare(b.item_name || ''),
      defaultSortOrder: 'ascend',
      render: (text) => <Text strong>{text || '-'}</Text>
    },
    {
      title: 'Category',
      dataIndex: 'category',
      key: 'category',
      align: 'center',
      sorter: (a, b) => (a.category || '').localeCompare(b.category || ''),
      render: (text) => (
        <Tag color="blue">{text || 'Uncategorized'}</Tag>
      )
    },
    {
      title: 'Balance Qty (Closing Stock)',
      dataIndex: 'balance_qty',
      key: 'balance_qty',
      align: 'center',
      sorter: (a, b) => parseFloat(a.balance_qty || 0) - parseFloat(b.balance_qty || 0),
      render: (text) => {
        const qty = parseFloat(text || 0);
        return (
          <Text strong style={{ color: qty === 0 ? '#ff4d4f' : qty < 10 ? '#faad14' : '#52c41a' }}>
            {qty.toFixed(2)}
          </Text>
        );
      }
    },
    {
      title: 'Avg Unit Price (฿)',
      dataIndex: 'price',
      key: 'price',
      align: 'center',
      sorter: (a, b) => parseFloat(a.price || 0) - parseFloat(b.price || 0),
      render: (text) => (
        <Text strong style={{ color: '#1890ff' }}>
          ฿{parseFloat(text || 0).toFixed(2)}
        </Text>
      )
    },
    {
      title: 'Total Amount (฿)',
      dataIndex: 'total_amount',
      key: 'total_amount',
      align: 'center',
      sorter: (a, b) => parseFloat(a.total_amount || 0) - parseFloat(b.total_amount || 0),
      render: (text) => (
        <Text strong style={{ color: '#52c41a', fontSize: '14px' }}>
          ฿{parseFloat(text || 0).toFixed(2)}
        </Text>
      )
    }
  ];

  // Get export columns based on checkbox selection
  const getExportColumns = () => {
    if (exportFullColumns) {
      // Full export with all columns
      return [
        { label: 'S.No.', key: 'sno' },
        { label: 'Item Name', key: 'item_name' },
        { label: 'Category', key: 'category' },
        { label: 'Closing Stock', key: 'balance_qty' },
        { label: 'Avg Price', key: 'price' },
        { label: 'Amount', key: 'total_amount' }
      ];
    } else {
      // Minimal export - only S.No., Item Name, and Closing Stock
      return [
        { label: 'S.No.', key: 'sno' },
        { label: 'Item Name', key: 'item_name' },
        { label: 'Closing Stock', key: 'balance_qty' }
      ];
    }
  };

  // Prepare export data with proper formatting
  const getExportData = () => {
    const exportData = [...filteredData]
      .sort((a, b) => (a.item_name || '').localeCompare(b.item_name || ''))
      .map((item, index) => {
        const baseData = {
          sno: index + 1,
          item_name: item.item_name || '-',
          balance_qty: parseFloat(item.balance_qty || 0).toFixed(2)
        };
        
        // Add additional columns only if exportFullColumns is true
        if (exportFullColumns) {
          return {
            ...baseData,
            category: item.category || 'Uncategorized',
            price: parseFloat(item.price || 0).toFixed(2),
            total_amount: parseFloat(item.total_amount || 0).toFixed(2)
          };
        }
        
        return baseData;
      });
    
    // Add total row when exporting full details
    if (exportFullColumns) {
      const totalStockAmount = filteredData.reduce((sum, item) => sum + parseFloat(item.total_amount || 0), 0);
      exportData.push({
        sno: '',
        item_name: 'TOTAL',
        category: '',
        balance_qty: '',
        price: '',
        total_amount: totalStockAmount.toFixed(2)
      });
    }
    
    return exportData;
  };

  const content = (
    <div style={{ padding: '24px', backgroundColor: '#f0f2f5', minHeight: '100vh' }}>
      <ToastContainer position="top-right" autoClose={3000} />

      {/* Statistics Cards */}
      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Total Items"
              value={totalItems}
              prefix={<ShoppingOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Total Quantity"
              value={totalQuantity.toFixed(2)}
              prefix={<InboxOutlined />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Total Value"
              value={totalValue.toFixed(2)}
              prefix={<DollarOutlined />}
              suffix="฿"
              valueStyle={{ color: '#faad14' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Zero Stock Items"
              value={zeroStockItems}
              prefix={<BarChartOutlined />}
              valueStyle={{ color: zeroStockItems > 0 ? '#ff4d4f' : '#52c41a' }}
            />
          </Card>
        </Col>
      </Row>

      {/* Filters Section */}
      <Card 
        title={
          <span>
            <FilterOutlined style={{ marginRight: 8 }} />
            Filters
          </span>
        }
        style={{ marginBottom: '24px' }}
      >
        <Row gutter={[16, 16]}>
          <Col xs={24} md={8}>
            <Text strong style={{ display: 'block', marginBottom: '8px' }}>
              Category
            </Text>
            <Select
              style={{ width: '100%' }}
              value={filters.category}
              onChange={(value) => handleFilterChange('category', value)}
              placeholder="Select category"
              size="large"
              allowClear
            >
              <Option value="">All Categories</Option>
              {getUniqueCategories().map((category, index) => (
                <Option key={index} value={category}>
                  {category}
                </Option>
              ))}
            </Select>
          </Col>
          <Col xs={24} md={8}>
            <Text strong style={{ display: 'block', marginBottom: '8px' }}>
              Search
            </Text>
            <Search
              placeholder="Search by item or category..."
              value={filters.searchText}
              onChange={(e) => handleFilterChange('searchText', e.target.value)}
              size="large"
              allowClear
            />
          </Col>
          <Col xs={24} md={8}>
            <Text strong style={{ display: 'block', marginBottom: '8px' }}>
              Actions
            </Text>
            <Space>
              <Button
                type="primary"
                icon={<FilterOutlined />}
                onClick={applyFilters}
                size="large"
              >
                Apply Filters
              </Button>
              <Button
                icon={<ReloadOutlined />}
                onClick={resetFilters}
                size="large"
              >
                Reset
              </Button>
            </Space>
          </Col>
        </Row>
        <Divider />
        <Row>
          <Col>
            <Space>
              <Button
                type={showZeroStock ? 'primary' : 'default'}
                onClick={() => {
                  setShowZeroStock(!showZeroStock);
                  setTimeout(() => applyFilters(), 0);
                }}
              >
                {showZeroStock ? 'Hide' : 'Show'} Zero Stock Items
              </Button>
            </Space>
          </Col>
        </Row>
      </Card>

      {/* Data Table with Export Options */}
      <Card
        title={
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <span>
                <BarChartOutlined style={{ marginRight: 8 }} />
                Closing Stock Report
              </span>
              <Tag color="gold" style={{ fontSize: '14px', padding: '6px 16px' }}>
                {filteredData.length} Items
              </Tag>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', alignItems: 'flex-end' }}>
              <Checkbox
                checked={exportFullColumns}
                onChange={(e) => setExportFullColumns(e.target.checked)}
                style={{ fontSize: '12px' }}
              >
                Export Full Details
              </Checkbox>
              <ExportButtons
                data={getExportData()}
                filename={`closing_stock_${new Date().toISOString().split('T')[0]}`}
                title="Closing Stock Report"
                columns={getExportColumns()}
                size="sm"
                pdfOrientation="portrait"
                logo={logo}
              />
            </div>
          </div>
        }
        style={{ 
          borderRadius: '12px',
          boxShadow: '0 4px 20px rgba(0,0,0,0.12)'
        }}
      >
        <Table
          dataSource={[...filteredData].sort((a, b) => (a.item_name || '').localeCompare(b.item_name || '')).map((item, index) => ({
            ...item,
            key: item.item_name || index
          }))}
          columns={columns}
          loading={loading}
          pagination={{
            current: currentPage,
            pageSize: itemsPerPage,
            total: filteredData.length,
            onChange: (page) => setCurrentPage(page),
            showSizeChanger: false,
            showTotal: (total, range) => `Showing ${range[0]} to ${range[1]} of ${total} items`,
            position: ['bottomCenter']
          }}
          scroll={{ x: 'max-content', y: 600 }}
          size="small"
          bordered
          rowClassName={(record) => {
            const qty = parseFloat(record.balance_qty || 0);
            return qty === 0 ? 'zero-stock-row' : qty < 10 ? 'low-stock-row' : '';
          }}
          footer={() => (
            <div style={{ 
              textAlign: 'right', 
              fontWeight: '700', 
              fontSize: '16px',
              padding: '12px',
              backgroundColor: '#f0f2f5',
              borderTop: '2px solid #d9d9d9'
            }}>
              <Text strong style={{ marginRight: '16px' }}>Total Value:</Text>
              <Text strong style={{ color: '#52c41a', fontSize: '18px' }}>
                ฿{totalValue.toFixed(2)}
              </Text>
            </div>
          )}
        />
        <style>{`
          .zero-stock-row {
            background-color: #fff1f0 !important;
          }
          .zero-stock-row:hover {
            background-color: #ffccc7 !important;
          }
          .low-stock-row {
            background-color: #fffbe6 !important;
          }
          .low-stock-row:hover {
            background-color: #fff566 !important;
          }
          .ant-table-tbody > tr:hover > td {
            background-color: #e6f7ff !important;
          }
        `}</style>
      </Card>
    </div>
  );

  return userType === 'wstoremanager' || userType === 'warehousestoremanager' ? (
    <Layout>
      <>
        {/* Floating Home Button */}
        <Button
          type="primary"
          icon={<HomeOutlined />}
          shape="circle"
          size="large"
          style={{ 
            position: 'fixed',
            right: 24, 
            bottom: 100,
            width: 56,
            height: 56,
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
            zIndex: 1000,
            backgroundColor: '#8B4513',
            borderColor: '#8B4513'
          }}
          onClick={() => navigate('/dashboard/warehousestoremanager')}
          title="Back to Dashboard"
        />
        
        {/* Floating Logout Button */}
        <Button
          type="primary"
          icon={<LogoutOutlined />}
          shape="circle"
          size="large"
          danger
          style={{ 
            position: 'fixed',
            right: 24, 
            bottom: 24,
            width: 56,
            height: 56,
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
            zIndex: 1000
          }}
          onClick={handleLogout}
          title="Logout"
        />
        {content}
      </>
    </Layout>
  ) : (
    <Layout>{content}</Layout>
  );
}
